import React, { useState, useContext, useEffect, useRef } from "react";
import MainLayout from "../../../components/layouts/MainLayout/MainLayout";
import { fetchAuthSession } from "@aws-amplify/auth";
import { v4 as uuidv4 } from "uuid";
import { DataContext } from "../../../context/DataContext";
import env from "../../../config/env";
import BubbleChat from "./components/BubbleChat"; // Asegúrate de importar correctamente

const ChatRoom = () => {
  const { API_URL, API_KEY, CHAT_URL } = env;
  const CHAT_BASE = React.useMemo(() => {
    let base =
      CHAT_URL ||
      API_URL ||
      "https://nbv4ejg6u4.execute-api.us-east-1.amazonaws.com/Prod/chat";
    base = base.replace(/\/$/, "");
    // Normaliza endpoints antiguos: sustituye solo el recurso, no la stage
    base = base.replace(/\/therapybot(\/.*)?$/i, "/chat");
    return base;
  }, [API_URL]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [chatLog, setChatLog] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [userInfo, setUserInfo] = useState({});
  const [nextCursor, setNextCursor] = useState(null);
  const { userData } = useContext(DataContext);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const authHeaders = async () => {
    try {
      const tokens = (await fetchAuthSession()).tokens;
      const idToken = tokens?.idToken?.toString();
      return idToken ? { Authorization: `Bearer ${idToken}` } : {};
    } catch (e) {
      console.error("auth header error", e);
      return {};
    }
  };

  const invokeGPTService = async (
    userMessage,
    chatLog,
    userName,
    userEmail,
    sessionId,
    userInfo
  ) => {
    
    const postData = {
      session_id: sessionId,
      user_email: userEmail,
      user_name: userName,
      question: userMessage,
      message_history: chatLog.map((log) => log.text),
    };

    const response = await fetch(CHAT_BASE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(API_KEY ? { "x-api-key": API_KEY } : {}),
        ...(await authHeaders()),
      },
      body: JSON.stringify(postData),
    });

    if (!response.ok) {
      throw new Error("Error en la solicitud a la API GPT");
    }

    const jsonResponse = await response.json();
    return jsonResponse.body;
  };

  useEffect(() => {
    if (!userData || !userData.attributes) return;
    const { attributes } = userData;
    setUserInfo(attributes);
    const savedSession = localStorage.getItem("chat_session_id") || uuidv4();
    setSessionId(savedSession);
    localStorage.setItem("chat_session_id", savedSession);
    // Cargar últimos 5 mensajes
    const loadHistory = async () => {
      setHistoryLoading(true);
      try {
        const url = new URL(`${CHAT_BASE}/history`);
        url.searchParams.set("email", attributes.email || "anon");
        url.searchParams.set("limit", "5");
        const res = await fetch(url.toString(), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(API_KEY ? { "x-api-key": API_KEY } : {}),
            ...(await authHeaders()),
          },
        });
        if (!res.ok) throw new Error("Historial no disponible");
        const data = await res.json();
        const items = data.items || [];
        const assembled = [];
        // items vienen newest-first; los invertimos para mostrar cronológico
        [...items].reverse().forEach((it) => {
          if (it.message) {
            assembled.push({ sender: "user", text: it.message, status: "Listo" });
          }
          if (it.response) {
            assembled.push({ sender: "gpt", text: it.response, status: "Listo" });
          }
        });
        // saludo por defecto si no hay historial
        if (assembled.length === 0) {
          assembled.push({
            sender: "gpt",
            text: "¡Hola! Soy tu asistente virtual. Estoy aquí para ayudarte. ¿En qué puedo asistirte hoy?",
            status: "Listo",
          });
        }
        setChatLog(assembled);
        setNextCursor(data.next_cursor || null);
      } catch (e) {
        console.error("load history", e);
        setChatLog([
          {
            sender: "gpt",
            text: "¡Hola! Soy tu asistente virtual. Estoy aquí para ayudarte. ¿En qué puedo asistirte hoy?",
            status: "Listo",
          },
        ]);
      } finally {
        setHistoryLoading(false);
      }
    };
    loadHistory();
  }, [userData, API_URL, API_KEY]);

  useEffect(() => {
    scrollToBottom();
  }, [chatLog]);

  const loadMoreHistory = async () => {
    if (!nextCursor) return;
    setHistoryLoading(true);
    try {
      const url = new URL(`${CHAT_BASE}/history`);
      url.searchParams.set("email", userInfo?.email || "anon");
      url.searchParams.set("limit", "5");
      url.searchParams.set("cursor", nextCursor);
      const res = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(API_KEY ? { "x-api-key": API_KEY } : {}),
          ...(await authHeaders()),
        },
      });
      if (!res.ok) throw new Error("No se pudo cargar más historial");
      const data = await res.json();
      const items = data.items || [];
      const assembled = [];
      [...items].reverse().forEach((it) => {
        if (it.message) assembled.push({ sender: "user", text: it.message, status: "Listo" });
        if (it.response) assembled.push({ sender: "gpt", text: it.response, status: "Listo" });
      });
      setChatLog((prev) => [...assembled, ...prev]);
      setNextCursor(data.next_cursor || null);
    } catch (e) {
      console.error("load more history", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (message.trim() === "") return;

    const newChatLog = [
      ...chatLog,
      { sender: "user", text: message, status: "esperando..." },
      { sender: "gpt", text: "escribiendo...", status: "esperando..." },
    ];
    setChatLog(newChatLog);
    setMessage("");
    setLoading(true);

    try {
      const response = await invokeGPTService(
        message,
        chatLog,
        userInfo?.name || "Usuario",
        userInfo?.email || "",
        sessionId,
        userInfo
      );

      const updatedChatLog = newChatLog.map((chat) =>
        chat.text === "escribiendo..." && chat.sender === "gpt"
          ? { ...chat, text: response, status: "Listo" }
          : chat
      );

      // Actualizar el estado del mensaje del usuario a "Listo"
      const finalChatLog = updatedChatLog.map((chat) =>
        chat.status === "esperando..." && chat.sender === "user"
          ? { ...chat, status: "Listo" }
          : chat
      );

      setChatLog(finalChatLog);
      // Desplazar hacia abajo tras recibir respuesta
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error("Error al enviar el mensaje:", error);
      setChatLog(newChatLog.filter((chat) => chat.text !== "escribiendo..."));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setMessage(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <MainLayout>
      <main className="flex flex-col flex-1 bg-gray-50 min-h-screen">
        <div className="flex-1 overflow-y-auto space-y-4 px-4 py-4 md:px-8 md:py-8">
          {nextCursor && (
            <div className="flex justify-center">
              <button
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                onClick={loadMoreHistory}
                disabled={historyLoading}
              >
                {historyLoading ? "Cargando..." : "Cargar más"}
              </button>
            </div>
          )}
          <h1 className="text-2xl font-bold mb-4">Chat</h1>
          {chatLog.map((chat, index) => {
            const senderName =
              chat.sender === "user"
                ? (userInfo?.name ? userInfo.name.split(" ")[0] : "Tú")
                : "SincronyBot";

            return (
              <BubbleChat
                key={index}
                position={chat.sender === "user" ? "right" : "left"}
                message={chat.text}
                status={chat.status}
                sender={senderName}
                userInfo={userInfo}
              />
            );
          })}
          <div ref={messagesEndRef} />
        </div>
        <div className="px-4 pb-6 md:px-8">
          <div className="flex items-center bg-white border border-gray-300 rounded-lg overflow-hidden shadow-sm">
            <textarea
              className="flex-1 px-4 py-4 md:py-6 border-none focus:outline-none resize-none rounded-none"
              rows={2}
              placeholder="Escribe tu mensaje..."
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            ></textarea>
            <button className="p-2" onClick={handleSendMessage}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-6 h-6"
              >
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      </main>
    </MainLayout>
  );
};

export default ChatRoom;
